import { useEffect, useState } from 'react';

export interface ToastItem {
  readonly id: string;
  readonly title?: string;
  readonly description?: string;
  readonly variant?: 'default' | 'destructive';
  readonly duration?: number;
}

type Listener = (toasts: ToastItem[]) => void;

const listeners = new Set<Listener>();
let memoryState: ToastItem[] = [];

function notify(): void {
  for (const listener of listeners) {
    listener(memoryState);
  }
}

export function toast(item: Omit<ToastItem, 'id'>): { id: string; dismiss: () => void } {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  memoryState = [...memoryState, { ...item, id }];
  notify();
  const duration = item.duration ?? 5000;
  if (duration > 0) {
    setTimeout(() => dismiss(id), duration);
  }
  return { id, dismiss: () => dismiss(id) };
}

export function dismiss(id: string): void {
  memoryState = memoryState.filter((t) => t.id !== id);
  notify();
}

export function useToast(): { toasts: ToastItem[]; toast: typeof toast; dismiss: typeof dismiss } {
  const [toasts, setToasts] = useState<ToastItem[]>(memoryState);
  useEffect(() => {
    const listener: Listener = (state) => setToasts(state);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
  return { toasts, toast, dismiss };
}
